import Workflow from 'models/Workflow';

// The Not Selected outcome's why, resolved to the client-configured labels (OLIO-3357): the reason,
// and the follow-up detail when the reason owed one (the free-typed competitor name, else the
// competitor's own lookup label). Returns null when the referral carries no reason — a Selected or
// all-Denied outcome tombstones `not_selected_reason` to nil, so absence is the correct signal that
// there is nothing to show.
//
// Labels fall back to the raw slug so a reason recorded against a since-archived or renamed lookup
// item still renders (mirrors `Workflow#lookupLabel` / the API's Narrate::NotSelectedDetail). The
// competitor detail has no slug fallback — an unresolvable competitor reads as no detail rather than
// a bare slug, matching how the slideout has always shown it.
//
// Shared by the Referral Outcome card and the SNF slideout so the resolution can't drift; each site
// formats the parts its own way (the card as "Reason – <reason>: <detail>", the slideout as
// "<reason> · Competitor: <detail>"). The reason's own typed detail rides unlabelled next to the
// reason at both, because it finishes that sentence rather than standing as a separate fact.
// Mirrors AcceptReferral::MAX_NOT_SELECTED_DETAIL in the api, which rejects anything longer. Held
// here so the form stops a long paste at the keyboard rather than at a 422 — the two have to move
// together, and there is nothing that would catch them drifting apart.
export const MAX_NOT_SELECTED_DETAIL = 120;

export interface NotSelectedReason {
  reasonLabel: string;
  detail: string | null;
  // The reason's OWN free text (OLIO-3419) — what it could not say on its own, like which assisted
  // living the patient went to. Separate from `detail` rather than sharing it: the slideout labels
  // that one "Competitor", which this is not. Null whenever the reason did not offer a box or
  // nobody typed in it, since the two are the same nothing to a reader.
  typedDetail: string | null;
}

export function resolveNotSelectedReason(workflow: Workflow): NotSelectedReason | null {
  const reason = workflow.valueFor('not_selected_reason')?.value as string | undefined;
  if (!reason) return null;

  const reasonLabel = workflow.lookupLabel('not_selected_reasons', reason) ?? reason;

  const competitor = workflow.valueFor('competitor')?.value as string | undefined;
  const competitorName = workflow.valueFor('competitor_name')?.value as string | undefined;
  const named = competitorName || workflow.lookupLabel('competitors', competitor);
  const detail = competitor && named ? named : null;

  // Read straight off the value rather than gated on the reason's current `offersDetail`. A reason
  // can have the flag turned off after somebody typed under it, and dropping what they wrote would
  // rewrite history — the same reason labels fall back to the raw slug above.
  const typedDetail = (workflow.valueFor('not_selected_detail')?.value as string | undefined) || null;

  return { reasonLabel, detail, typedDetail };
}
